import { useEffect, useState } from 'react'
import { AdminPanelPage } from '../AdminPanelPage'
import { BreadcrumbProps, useToast } from '@aidkitorg/component-library'
import { useLocalizedStrings } from '../../Localization'
import { useHistory } from 'react-router-dom'
import { QueueCard } from './components/QueueCard'
import { usePost } from '../../API'
import { logAdminPanelError } from '../../utils/adminPanel.utils'
import * as survey from '@aidkitorg/types/lib/survey'
import { openWindowWithLoadingContent } from '../../LoadingTabUtil'

type QueueStats = {
  name: string;
  description?: string;
  assigneeField: string;
  startSection: string;
  in_queue: number;
  in_review: number;
  completed: number;
  queueObject: survey.Queue;
  hasSubmitCompleteField: boolean;
};

export const ReviewQueuesPage = () => {
  const L = useLocalizedStrings()
  const history = useHistory()
  const { toast } = useToast()
  const [queues, setQueues] = useState<QueueStats[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const getQueuesStatsPost = usePost("/api/admin_panel/review_queues_stats")
  const takeApplicantPost = usePost('/queues/take_applicant')

  const fetchQueuesStats = async (silent = false) => {
    if (!silent) {
      setIsLoading(true)
    }

    const response = await getQueuesStatsPost({})

    if (response.error || !response.data) {
      if (response.error) {
        logAdminPanelError(response.error, 'Failed to fetch queue stats')
      }
      setIsLoading(false)
      return
    }

    setQueues(response.data.queues || [])
    setIsLoading(false)
  }

  // Fetch stats on mount and poll every 10 seconds
  useEffect(() => {
    fetchQueuesStats()

    const interval = setInterval(() => {
      fetchQueuesStats(true) // silent refresh
    }, 10000)

    return () => clearInterval(interval)
  }, [])

  const handleClaimApplicant = async (queue: QueueStats) => {
    if (window.confirm(L.dashboard.claim_applicant_confirmation)) {
      const win = openWindowWithLoadingContent()

      try {
        const resp = await takeApplicantPost({ queue: queue.queueObject })

        if ('applicant' in resp) {
          const appPath = '/admin-panel/a/'
          const startingSection = queue.startSection
          const loc = appPath + resp.applicant + (startingSection ? '/' + startingSection : '')

          if (win) {
            win.location.href = loc
          } else {
            history.push(loc)
          }

          toast({
            description: L.dashboard.applicant_claimed,
            variant: 'success'
          })

          // Refresh stats
          fetchQueuesStats(true)
        } else {
          toast({
            description: L.dashboard.claim_applicant_error + ' ' + resp.error,
            variant: 'error'
          })
          win?.close()
        }
      } catch (e) {
        console.warn("Error claiming applicant: ", e)
        toast({
          description: L.dashboard.claim_applicant_error,
          variant: 'error'
        })
        win?.close()
      }
    }
  }

  const breadcrumbs: BreadcrumbProps['items'] = [
    {
      label: L.admin_panel.nav.reviews,
      href: '/admin-panel/reviews/queues'
    },
    {
      label: L.admin_panel.nav.review_queues
    }
  ]

  return (
    <AdminPanelPage
      title={L.admin_panel.nav.review_queues}
      breadcrumbs={breadcrumbs}
    >
      <div className="flex flex-col gap-6">
        {queues.map((queue) => (
          <QueueCard
            key={queue.assigneeField}
            name={queue.name}
            description={queue.description}
            inQueue={queue.in_queue}
            inReview={queue.in_review}
            completed={queue.completed}
            onClaimApplicant={() => handleClaimApplicant(queue)}
            isLoading={isLoading && queues.length === 0}
            hasSubmitCompleteField={queue.hasSubmitCompleteField}
          />
        ))}

        {!isLoading && queues.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">{L.admin_panel.reviews.queues_coming_soon}</p>
          </div>
        )}
      </div>
    </AdminPanelPage>
  )
}
